import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isQueryMountedWithData } from '@/hooks/api/sse/utils.ts';
import { RUN_CHAT_MESSAGES_KEY } from '@/utils/queryKeys.ts';
import { ChatMessage, ChatMessages } from '@/types/runs.ts';

const useInvalidateRunChatMessages = (automationId: string, runId: string) => {
  const queryClient = useQueryClient();

  return useCallback(
    (newMessage: ChatMessage): unknown[][] => {
      const queryKey = RUN_CHAT_MESSAGES_KEY(automationId, runId);

      if (!isQueryMountedWithData(queryClient, queryKey)) {
        return [queryKey];
      }

      queryClient.setQueryData<ChatMessages>(queryKey, (data) => {
        const currentMessages = data?.results ?? [];
        let found = false;

        const updatedMessages = currentMessages.map((msg) => {
          if (msg.id === newMessage.id) {
            found = true;
            return { ...msg, ...newMessage };
          }
          return msg;
        });

        if (!found) {
          updatedMessages.push(newMessage);
        }

        updatedMessages.sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        );

        return {
          count: data?.count ?? updatedMessages.length,
          next: data?.next ?? null,
          previous: data?.previous ?? null,
          results: updatedMessages,
        };
      });

      return [];
    },
    [automationId, queryClient, runId],
  );
};

export default useInvalidateRunChatMessages;
